import React, { useState, useEffect, useRef } from "react";
import { ArrowRight, Link, Zap } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Badge as JSBadge } from "./badge";
import { Button as JSButton } from "./button";
import { Card as JSCard, CardContent as JSCardContent, CardHeader as JSCardHeader, CardTitle as JSCardTitle } from "./card";

const Badge = JSBadge as any;
const Button = JSButton as any;
const Card = JSCard as any;
const CardContent = JSCardContent as any;
const CardHeader = JSCardHeader as any;
const CardTitle = JSCardTitle as any;


export interface TimelineItem {
  id: number;
  title: string;
  icon: React.ComponentType<any>;
  content: string;
  status: string;
  date: string;
  energy: number;
  relatedIds: number[];
  category?: string;
}

export interface RadialOrbitalTimelineProps {
  timelineData: TimelineItem[];
}

export default function RadialOrbitalTimeline({ timelineData }: Readonly<RadialOrbitalTimelineProps>) {
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>({});
  const [viewMode, setViewMode] = useState<string>("orbital");
  const [rotationAngle, setRotationAngle] = useState<number>(0);
  const [autoRotate, setAutoRotate] = useState<boolean>(true);
  const [pulseEffect, setPulseEffect] = useState<Record<number, boolean>>({});
  const [centerOffset, setCenterOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [activeNodeId, setActiveNodeId] = useState<number | null>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [windowWidth, setWindowWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const containerRef = useRef<HTMLDivElement>(null);
  const orbitRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === containerRef.current || e.target === orbitRef.current) {
      setExpandedItems({});
      setActiveNodeId(null);
      setPulseEffect({});
      setAutoRotate(true);
    }
  };

  const getRelatedItems = (itemId: number): number[] => {
    const currentItem = timelineData.find((item) => item.id === itemId);
    return currentItem ? currentItem.relatedIds : [];
  };

  const isRelated = (sourceId: number, targetId: number) => {
    const source = timelineData.find((item) => item.id === sourceId);
    return source?.relatedIds?.includes(targetId) ?? false;
  };

  const centerViewOnNode = (nodeId: number) => {
    if (viewMode !== "orbital" || !nodeRefs.current[nodeId]) return;

    const nodeIndex = timelineData.findIndex((item) => item.id === nodeId);
    const totalNodes = timelineData.length;
    const targetAngle = (nodeIndex / totalNodes) * 360;

    let newAngle = 270 - targetAngle;
    while (newAngle < 0) newAngle += 360;
    setRotationAngle(newAngle % 360);
  };

  const toggleItem = (id: number) => {
    setExpandedItems((prev) => {
      const newState = { ...prev };
      Object.keys(newState).forEach((key) => {
        const numKey = Number.parseInt(key, 10);
        if (numKey !== id) {
          newState[numKey] = false;
        }
      });

      newState[id] = !prev[id];

      if (!prev[id]) {
        setActiveNodeId(id);
        setAutoRotate(false);

        const relatedItems = getRelatedItems(id);
        const newPulseEffect: Record<number, boolean> = {};
        relatedItems.forEach((relId) => {
          newPulseEffect[relId] = true;
        });
        setPulseEffect(newPulseEffect);

        centerViewOnNode(id);
      } else {
        setActiveNodeId(null);
        setAutoRotate(true);
        setPulseEffect({});
      }

      return newState;
    });
  };

  useEffect(() => {
    let animationFrameId: number;
    let lastTime = performance.now();

    const animate = (time: number) => {
      let deltaTime = time - lastTime;
      lastTime = time;

      // Cap delta time to prevent huge jumps when returning to the tab
      if (deltaTime > 50) deltaTime = 16;

      setRotationAngle((prev) => {
        let newAngle = (prev + (6 / 1000) * deltaTime) % 360;
        if (newAngle < 0) newAngle += 360;
        return newAngle;
      });
      animationFrameId = requestAnimationFrame(animate);
    };

    if (autoRotate && viewMode === "orbital") {
      lastTime = performance.now();
      animationFrameId = requestAnimationFrame(animate);
    }

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [autoRotate, viewMode]);

  // Auto-activate the node closest to 3 o'clock (0 degrees)
  useEffect(() => {
    if (!autoRotate) return;

    let closestNodeId: number | null = null;
    let minDistance = 360;

    timelineData.forEach((item, index) => {
      const angle = ((index / timelineData.length) * 360 + rotationAngle) % 360;
      // Distance to 0 (3 o'clock)
      const dist = Math.min(angle, 360 - angle);
      if (dist < minDistance) {
        minDistance = dist;
        closestNodeId = item.id;
      }
    });

    if (closestNodeId && closestNodeId !== activeNodeId) {
      setActiveNodeId(closestNodeId);
      setExpandedItems({ [closestNodeId]: true });

      const currentItem = timelineData.find((item) => item.id === closestNodeId);
      const relatedIds = currentItem ? currentItem.relatedIds : [];

      const newPulseEffect: Record<number, boolean> = {};
      relatedIds.forEach((relId) => {
        newPulseEffect[relId] = true;
      });
      setPulseEffect(newPulseEffect);
    }
  }, [rotationAngle, timelineData, activeNodeId, autoRotate]);

  const calculateNodePosition = (index: number, total: number) => {
    const angle = ((index / total) * 360 + rotationAngle) % 360;
    const radius = windowWidth < 768 ? 140 : 320;
    const radian = (angle * Math.PI) / 180;

    const x = radius * Math.cos(radian) + centerOffset.x;
    const y = radius * Math.sin(radian) + centerOffset.y;

    const zIndex = Math.round(100 + 50 * Math.cos(radian));
    const opacity = Math.max(
      0.4,
      Math.min(1, 0.4 + 0.6 * ((1 + Math.sin(radian)) / 2))
    );

    return { x, y, angle, zIndex, opacity };
  };

  const isRelatedToActive = (itemId: number) => {
    if (!activeNodeId) return false;
    const relatedItems = getRelatedItems(activeNodeId);
    return relatedItems.includes(itemId);
  };

  return (
    <div
      className="w-full flex flex-col items-center justify-center overflow-hidden min-h-[900px] md:h-[85vh] md:min-h-[800px]"
      style={{
        background: '#101826',
      }}
      ref={containerRef}
      onClick={handleContainerClick}
    >
      <div className="relative w-full max-w-[1400px] h-full flex flex-col md:flex-row items-center justify-start md:justify-center pt-4 md:pt-0">

        {/* Fixed Side Panel for Active Node */}
        <div className="order-2 md:order-1 relative md:absolute md:right-8 lg:-right-24 md:top-1/2 md:-translate-y-1/2 mt-12 md:mt-16 mb-16 md:mb-0 w-[92vw] sm:w-[400px] md:w-[500px] lg:w-[600px] z-50 pointer-events-auto shrink-0">
          <AnimatePresence mode="wait">
            {activeNodeId && (() => {
              const item = timelineData.find(i => i.id === activeNodeId);
              if (!item) return null;

              let statusText = "STANDBY";
              let statusBadgeClass = "bg-slate-500/10 text-slate-400 border border-slate-500/20";

              if (item.status === "completed") {
                statusText = "● ACTIVE";
                statusBadgeClass = "bg-violet-500/10 text-violet-400 border border-violet-500/20";
              } else if (item.status === "in-progress") {
                statusText = "○ PROCESSING";
                statusBadgeClass = "bg-sky-500/10 text-sky-400 border border-sky-500/20";
              }

              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: -20, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 20, scale: 0.98 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Card
                    className="w-full min-h-[500px] flex flex-col justify-center shadow-[0_20px_50px_rgba(96,165,250,0.15)] overflow-visible rounded-3xl relative"
                    style={{
                      background: 'rgba(8,11,12,0.88)',
                      border: '1px solid rgba(200,230,255,0.12)',
                      color: '#ffffff',
                      backdropFilter: 'blur(22px)',
                    }}
                  >
                    {/* Top gradient accent line */}
                    <div
                      className="absolute top-0 left-0 w-full h-1.5 rounded-t-2xl"
                      style={{
                        background: 'linear-gradient(90deg, #38bdf8, #60a5fa, #8b5cf6)',
                      }}
                    ></div>

                    <CardHeader className="pb-5 pt-10 px-8">
                      <div className="flex justify-between items-center">
                        <Badge className={`px-3 py-1 text-xs font-bold border-0 shadow-sm ${statusBadgeClass}`}>
                          {statusText}
                        </Badge>
                        <span className="text-xs font-mono font-bold text-slate-400 bg-white/5 px-2.5 py-1 rounded-md shadow-sm border border-white/10">{item.date}</span>
                      </div>
                      <CardTitle className="text-2xl md:text-3xl mt-4 md:mt-5 font-black text-white flex items-center gap-4 tracking-tight">
                        <div className="p-3 bg-gradient-to-br from-blue-500/10 to-violet-500/10 rounded-xl shadow-inner border border-blue-500/25">
                          <item.icon size={24} className="text-blue-400 md:w-7 md:h-7" />
                        </div>
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm md:text-lg font-medium text-slate-300 leading-relaxed md:leading-loose px-6 md:px-8 pb-8 md:pb-10">
                      <p>{item.content}</p>

                      <div className="hidden md:block mt-8 pt-6 border-t border-white/10">
                        <div className="flex justify-between items-center text-sm font-bold mb-3">
                          <span className="flex items-center text-slate-300">
                            <Zap size={18} className="mr-2 text-cyan-400 fill-cyan-400" />
                            System Energy
                          </span>
                          <span className="font-mono text-xl font-black text-white">{item.energy}%</span>
                        </div>
                        <div className="w-full h-2.5 bg-white/5 rounded-full overflow-hidden shadow-inner border border-white/5">
                          <div
                            className="h-full bg-gradient-to-r from-blue-500 via-cyan-400 to-violet-500"
                            style={{ width: `${item.energy}%` }}
                          ></div>
                        </div>
                      </div>

                      {item.relatedIds.length > 0 && (
                        <div className="hidden md:block mt-6 pt-6 border-t border-white/10">
                          <div className="flex items-center mb-4">
                            <Link size={16} className="text-slate-400 mr-2" />
                            <h4 className="text-xs uppercase tracking-widest font-bold text-slate-400">
                              Integrated Nodes
                            </h4>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {item.relatedIds.map((relatedId) => {
                              const relatedItem = timelineData.find((i) => i.id === relatedId);
                              if (!relatedItem) return null;
                              return (
                                <Button
                                  key={relatedId}
                                  variant="outline"
                                  size="sm"
                                  className="flex items-center h-10 px-4 py-2 text-sm font-bold rounded-xl border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white hover:border-white/20 transition-all shadow-sm"
                                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                    e.stopPropagation();
                                    toggleItem(relatedId);
                                  }}
                                >
                                  {relatedItem.title}
                                  <ArrowRight size={14} className="ml-2 text-cyan-400" />
                                </Button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })()}
          </AnimatePresence>
        </div>

        <div
          className="order-1 md:order-2 relative md:absolute w-full h-[360px] md:h-full flex items-center justify-center ml-0 md:-ml-[700px] shrink-0"
          ref={orbitRef}
          style={{
            perspective: "1000px",
            transform: `translate(${centerOffset.x}px, ${centerOffset.y}px)`,
          }}
        >
          <div
            className="absolute w-24 h-24 rounded-full flex flex-col items-center justify-center z-10"
            style={{
              background:
                'linear-gradient(135deg, #2563eb 0%, #3b82f6 45%, #7c3aed 100%)',
              boxShadow:
                hoveredId !== null ? '0 0 35px rgba(56,189,248,0.5)' : '0 0 45px rgba(96,165,250,0.28)',
              transform: hoveredId !== null ? 'scale(1.08)' : 'scale(1)',
              transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.3s ease',
            }}
          >
            <div
              className="absolute w-32 h-32 rounded-full animate-pulse"
              style={{
                border: '1px solid rgba(125,211,252,0.30)',
              }}
            />

            <div
              className="absolute w-40 h-40 rounded-full animate-pulse"
              style={{
                border: '1px solid rgba(125,211,252,0.14)',
              }}
            />

            <div
              style={{
                textAlign: 'center',
                lineHeight: 1,
                color: '#ffffff',
                fontFamily: 'Bebas Neue, sans-serif',
                letterSpacing: '0.07em',
              }}
            >
              <div
                style={{
                  fontSize: '20px',
                }}
              >
                GLIEDAI
              </div>

              <div
                style={{
                  marginTop: 5,
                  fontFamily: 'Space Mono, monospace',
                  fontSize: '6px',
                  letterSpacing: '0.14em',
                  color: '#c8e6ff',
                }}
              >
                CORE INTELLIGENCE
              </div>
            </div>
          </div>

          <div
            className="absolute rounded-full"
            style={{
              width: windowWidth < 768 ? '280px' : '640px',
              height: windowWidth < 768 ? '280px' : '640px',
              border: '1px solid rgba(125,211,252,0.10)',
              boxShadow: '0 0 40px rgba(56,189,248,0.025)',
            }}
          ></div>

          {timelineData.map((item, index) => {
            const position = calculateNodePosition(index, timelineData.length);
            const isExpanded = expandedItems[item.id];
            const isRelatedNode = isRelatedToActive(item.id);
            const isPulsing = pulseEffect[item.id];
            const Icon = item.icon;

            const isHovered = hoveredId === item.id;
            const isConnected = hoveredId !== null && isRelated(hoveredId, item.id);
            const isDimmed = hoveredId !== null && !isHovered && !isConnected;

            const nodeStyle = {
              transform: `translate(${position.x}px, ${position.y}px) ${isHovered ? 'scale(1.15)' : 'scale(1)'}`,
              zIndex: isExpanded || isHovered ? 200 : position.zIndex,
              opacity: isDimmed ? 0.28 : (isExpanded ? 1 : position.opacity),
              transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease',
            };

            let nodeInnerStyle: React.CSSProperties = {};
            let nodeIconClass = "";
            let nodeClassNames = "";

            if (isExpanded) {
              nodeInnerStyle = {
                background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                borderColor: "#c8e6ff",
                boxShadow: isHovered
                  ? "0 0 24px rgba(56,189,248,0.75)"
                  : "0 0 20px rgba(96,165,250,0.5)",
              };
              nodeIconClass = "text-white";
            } else if (isHovered) {
              nodeInnerStyle = {
                background: "linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)",
                borderColor: "#38bdf8",
                boxShadow: "0 0 24px rgba(56,189,248,0.75)",
              };
              nodeIconClass = "text-white";
            } else if (isConnected) {
              nodeInnerStyle = {
                background: "rgba(56,189,248,0.22)",
                borderColor: "rgba(56,189,248,0.55)",
                boxShadow: "0 0 14px rgba(96,165,250,0.35)",
              };
              nodeIconClass = "text-[#c8e6ff]";
              nodeClassNames = "animate-pulse";
            } else if (isRelatedNode) {
              nodeInnerStyle = {
                background: "rgba(96,165,250,0.16)",
                borderColor: "rgba(96,165,250,0.4)",
              };
              nodeIconClass = "text-[#c8e6ff]";
              nodeClassNames = "animate-pulse";
            } else {
              nodeInnerStyle = {
                background: "#ffffff",
                borderColor: "rgba(58, 100, 120, 0.3)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              };
              nodeIconClass = "text-[#3a6478]";
            }

            return (
              <button
                key={item.id}
                type="button"
                ref={(el) => {
                  nodeRefs.current[item.id] = el;
                }}
                className={`absolute cursor-pointer border-0 bg-transparent p-0 outline-none ${!autoRotate ? "transition-all duration-700" : ""}`}
                style={nodeStyle}
                onMouseEnter={() => setHoveredId(item.id)}
                onMouseLeave={() => setHoveredId(null)}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleItem(item.id);
                }}
              >
                <div
                  className={`absolute rounded-full -inset-1 ${isPulsing ? "animate-pulse duration-1000" : ""
                    }`}
                  style={{
                    background: `radial-gradient(circle, rgba(125, 211, 252, 0.20) 0%, rgba(125, 211, 252, 0) 70%)`,
                    width: `${item.energy * 0.5 + 40}px`,
                    height: `${item.energy * 0.5 + 40}px`,
                    left: `-${(item.energy * 0.5 + 40 - 40) / 2}px`,
                    top: `-${(item.energy * 0.5 + 40 - 40) / 2}px`,
                  }}
                ></div>

                <div
                  className={`
                  w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center
                  border-2 
                  transition-all duration-300 transform
                  ${isExpanded ? "scale-125" : ""}
                  ${nodeClassNames}
                `}
                  style={nodeInnerStyle}
                >
                  <Icon size={24} className={nodeIconClass} />
                </div>

                <div
                  className={`
                  absolute top-14 md:top-20 whitespace-nowrap
                  text-[10px] md:text-sm font-bold tracking-wider
                  transition-all duration-300
                  ${isExpanded || isHovered ? "text-[#c8e6ff] scale-110 drop-shadow-[0_0_8px_rgba(96,165,250,0.5)]" : "text-slate-400"}
                  ${isDimmed ? "opacity-30" : "opacity-100"}
                `}
                >
                  {item.title}
                </div>

              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
